import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllPartenaires,
  addPartenaire,
  updatePartenaire,
  deletePartenaire,
  updatePartenaireOrder,
  updatePartenaireActivation,
} from "../../../../../../../services/partenairesServices";

import {
  uploadPartenairePhoto1,
  uploadPartenairePhoto2,
  uploadPartenairePhoto3,
} from "../../../../../../../services/partenairesPhotoServices";

import {
  buildPartenairePayload,
  calculatePartenairesStats,
  filterPartenaires,
  getNextPartenaireOrder,
  normalizeOrderPartenaires,
  sortPartenairesByOrder,
  validateImageFile,
  validatePartenairePayload,
} from "../utils/partenairesSettingsUtils";

const uploaders = {
  photo_url: uploadPartenairePhoto1,
  photo2_url: uploadPartenairePhoto2,
  photo3_url: uploadPartenairePhoto3,
};

export function usePartenairesSettings() {
  const [partenaires, setPartenaires] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    query: "",
    visibility: "all",
    detail: "all",
  });

  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [editablePartenaire, setEditablePartenaire] = useState(null);
  const [submittingForm, setSubmittingForm] = useState(false);

  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [reordering, setReordering] = useState(false);

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadingPartenaire, setUploadingPartenaire] = useState(null);
  const [uploadTarget, setUploadTarget] = useState("photo_url");
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const fetchPartenaires = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getAllPartenaires();
      setPartenaires(sortPartenairesByOrder(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error("Erreur chargement partenaires :", error);
      toast.error("Impossible de charger les partenaires.");
      setPartenaires([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPartenaires();
  }, [fetchPartenaires]);

  useEffect(() => {
    if (!selectedFile) {
      setFilePreview("");
      return undefined;
    }

    const url = URL.createObjectURL(selectedFile);
    setFilePreview(url);

    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const filteredPartenaires = useMemo(() => {
    return filterPartenaires(partenaires, filters);
  }, [partenaires, filters]);

  const stats = useMemo(
    () => calculatePartenairesStats(partenaires),
    [partenaires]
  );

  const filteredStats = useMemo(
    () => calculatePartenairesStats(filteredPartenaires),
    [filteredPartenaires]
  );

  const nextOrder = useMemo(
    () => getNextPartenaireOrder(partenaires),
    [partenaires]
  );

  const setFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetFilters = () => {
    setFilters({
      query: "",
      visibility: "all",
      detail: "all",
    });
  };

  const openCreateDrawer = () => {
    setFormMode("create");
    setEditablePartenaire(null);
    setFormDrawerOpen(true);
  };

  const openEditDrawer = (partenaire) => {
    setFormMode("edit");
    setEditablePartenaire(partenaire || null);
    setFormDrawerOpen(true);
  };

  const closeFormDrawer = () => {
    if (submittingForm) return;

    setFormDrawerOpen(false);
    setEditablePartenaire(null);
    setFormMode("create");
  };

  const handleSubmitPartenaire = async (formData) => {
    const payload = buildPartenairePayload(formData);
    const errorMessage = validatePartenairePayload(payload);

    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    try {
      setSubmittingForm(true);

      if (formMode === "create") {
        await addPartenaire(payload);
        toast.success("Partenaire ajouté avec succès.");
      } else {
        if (!editablePartenaire?.id) {
          toast.error("Partenaire introuvable.");
          return;
        }

        await updatePartenaire(editablePartenaire.id, payload);
        toast.success("Partenaire mis à jour avec succès.");
      }

      await fetchPartenaires();
      closeFormDrawer();
    } catch (error) {
      console.error("Erreur enregistrement partenaire :", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Erreur lors de l’enregistrement du partenaire."
      );
    } finally {
      setSubmittingForm(false);
    }
  };

  const handleDeletePartenaire = async (partenaire) => {
    if (!partenaire?.id) return;

    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir supprimer « ${
          partenaire.nom || "ce partenaire"
        } » ?`
      )
    ) {
      return;
    }

    try {
      setDeletingId(partenaire.id);

      await deletePartenaire(partenaire.id);

      toast.success("Partenaire supprimé avec succès.");
      await fetchPartenaires();
    } catch (error) {
      console.error("Erreur suppression partenaire :", error);
      toast.error("Erreur lors de la suppression du partenaire.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (partenaire) => {
    if (!partenaire?.id) return;

    try {
      setTogglingId(partenaire.id);

      await updatePartenaireActivation(partenaire.id, {
        is_active: !partenaire.is_active,
      });

      setPartenaires((prev) =>
        prev.map((item) =>
          item.id === partenaire.id
            ? { ...item, is_active: !item.is_active }
            : item
        )
      );

      toast.success("Visibilité du partenaire mise à jour.");
    } catch (error) {
      console.error("Erreur activation partenaire :", error);
      toast.error("Erreur lors de la mise à jour du statut du partenaire.");
      await fetchPartenaires();
    } finally {
      setTogglingId(null);
    }
  };

  const applyReorder = async (rows) => {
    const orderedRows = normalizeOrderPartenaires(rows);

    try {
      setReordering(true);
      setPartenaires(orderedRows);

      await updatePartenaireOrder(orderedRows);

      toast.success("Ordre des partenaires mis à jour.");
      await fetchPartenaires();
    } catch (error) {
      console.error("Erreur ordre partenaires :", error);
      toast.error("Erreur lors de la mise à jour de l’ordre.");
      await fetchPartenaires();
    } finally {
      setReordering(false);
    }
  };

  const movePartenaire = async (partenaireId, direction) => {
    if (reordering || loading) return;

    const rows = sortPartenairesByOrder(partenaires);
    const index = rows.findIndex((partenaire) => partenaire.id === partenaireId);

    if (index === -1) return;

    const targetIndex = direction === "up" ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= rows.length) return;

    const nextRows = [...rows];
    const current = nextRows[index];

    nextRows[index] = nextRows[targetIndex];
    nextRows[targetIndex] = current;

    await applyReorder(nextRows);
  };

  const openUploadModal = (partenaire, target = "photo_url") => {
    setUploadingPartenaire(partenaire || null);
    setUploadTarget(target || "photo_url");
    setSelectedFile(null);
    setFilePreview("");
    setUploadProgress(0);
    setUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    if (uploading) return;

    setUploadModalOpen(false);
    setUploadingPartenaire(null);
    setUploadTarget("photo_url");
    setSelectedFile(null);
    setFilePreview("");
    setUploadProgress(0);
  };

  const handleUploadFileChange = (event) => {
    const file = event.target.files?.[0] || null;

    if (!file) {
      setSelectedFile(null);
      return;
    }

    const errorMessage = validateImageFile(file);

    if (errorMessage) {
      toast.error(errorMessage);
      event.target.value = "";
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const handleUploadSubmit = async (event) => {
    event?.preventDefault?.();

    if (!uploadingPartenaire?.id) {
      toast.error("Partenaire introuvable.");
      return;
    }

    const errorMessage = validateImageFile(selectedFile);

    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    const uploadFn = uploaders[uploadTarget];

    if (!uploadFn) {
      toast.error("Type d’image invalide.");
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      await uploadFn(uploadingPartenaire.id, selectedFile, {
        format: "webp",
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;

          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );

          setUploadProgress(percent);
        },
      });

      toast.success("Image du partenaire mise à jour avec succès.");
      await fetchPartenaires();
      closeUploadModal();
    } catch (error) {
      console.error("Erreur upload partenaire :", error);
      toast.error("Erreur lors de l’upload de l’image.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return {
    partenaires,
    filteredPartenaires,
    loading,

    filters,
    setFilter,
    resetFilters,

    stats,
    filteredStats,
    nextOrder,

    formDrawerOpen,
    formMode,
    editablePartenaire,
    submittingForm,
    openCreateDrawer,
    openEditDrawer,
    closeFormDrawer,
    handleSubmitPartenaire,

    deletingId,
    togglingId,
    reordering,
    movePartenaire,
    handleDeletePartenaire,
    handleToggleActive,

    uploadModalOpen,
    uploadingPartenaire,
    uploadTarget,
    setUploadTarget,
    selectedFile,
    filePreview,
    uploading,
    uploadProgress,
    openUploadModal,
    closeUploadModal,
    handleUploadFileChange,
    handleUploadSubmit,

    fetchPartenaires,
  };
}